import { addLabels } from '@otto-ec/global-resources/apm';
import { OcCarouselV1Events } from '@otto-ec/otto-components/carousel';
import { ImagesSubComponent } from '../images.sub.component';
import { isMobile } from '../../../_common/client/breakpoint';
import { getImageUrl } from '../../../_common/client/images';
import { MainImageVideo } from './main-image-video';
import { ZoomImageElement } from '../../../_feature-components/zoom-image/zoom-image.element';
import zoomImageTemplate from '../../partials/main-image-zoom-image.hbs';
import { trackEvent } from '@otto-ec/nexus-registry/pdp/tracking';

export class MainImageSubComponent extends ImagesSubComponent {
    public rootSelector = '.js_pdp_main-image';

    private imageHasBeenChanged = false;

    private currentIndex = 0;

    private video?: MainImageVideo;

    private seoImageName = '';

    public async onHtmlLoaded(): Promise<void> {
        if (this.isPresent()) {
            this.seoImageName = this.getRootElement().dataset.seoImageName || '';
            this.currentIndex = 0;
            this.getCarouselElement().addEventListener('oc-item-visibility-changed', event => this.handleItemVisibilityChangedEvent(event));
            if (isMobile()) {
                this.activatePageIndicator();
            }
            this.initializeVideo();
            this.handleSlideChange(0, false);
            this.imageHasBeenChanged = false;
            this.setTrackingPinchzoomEventForZoomImage();
            this.sendPerformanceDataToApm();
        }
    }

    private sendPerformanceDataToApm(retryCount = 0) {
        if (!window.performance || !window.performance.getEntriesByType) {
            return;
        }
        const firstImgElement = this.getRootElement().querySelector('img');
        if (!firstImgElement) {
            return;
        }
        const currentSrc = firstImgElement.currentSrc;
        const entryForImage = window.performance.getEntriesByType('resource').filter(entry => entry.name === currentSrc)[0];
        if (entryForImage) {
            addLabels({
                firstMainImageDuration: entryForImage.duration,
                firstMainImageName: entryForImage.name,
                fistMainImageStartTime: entryForImage.startTime,
            });
        } else if (retryCount < 20) {
            setTimeout(() => this.sendPerformanceDataToApm(retryCount + 1), 500);
        }
    }

    private setTrackingPinchzoomEventForZoomImage() {
        o_util.event.delegate(this.getRootElement(), ZoomImageElement.EVENT_OPENED, '.js_pdp_main-image__zoom-image', () => {
            trackEvent({
                product_DifferentImageActivity: ['pinchzoom'],
            });
        });
    }

    protected onItemChanged(index: number): void {
        this.getCarouselElement().moveTo(index, { reducedMotion: true });

        this.trackStageChange(index, 'thumbnail');
    }

    private activatePageIndicator() {
        this.getCarouselElement().removeAttribute('hide-pagination-dots');
    }

    private initializeVideo() {
        if (!this.hasVideo()) return;

        const videoAssetKey = this.getVideoAssetKey();
        if (!videoAssetKey) return;

        const slideElements = this.getChildElements<HTMLElement>('.js_pdp_main-image__slide');
        const { slideElement, slideContentElement } = this.createSlide();
        slideElement.setAttribute('data-oc-carousel-v1.item-type', 'video');
        slideElements[this.getVideoIndex() - 1].insertAdjacentElement('afterend', slideElement);

        this.video = new MainImageVideo(videoAssetKey, slideContentElement);
    }

    private createSlide() {
        const slideElement = document.createElement('div');
        slideElement.className = 'pdp_main-image__slide js_pdp_main-image__slide js_pdp_main-image__slide--video';

        const slideContentWrapperElement = document.createElement('div');
        slideContentWrapperElement.className = 'pdp_main-image__slide-content-wrapper';

        const slideContentElement = document.createElement('div');
        slideContentElement.className = 'pdp_main-image__slide-content js_pdp_main-image__slide-content';

        slideContentWrapperElement.appendChild(slideContentElement);
        slideElement.appendChild(slideContentWrapperElement);

        return {
            slideElement,
            slideContentElement,
        };
    }

    private handleSlideChange(currentIndex: number, emitToOtherSubcomponents: boolean) {
        const previousIndex = this.currentIndex;
        const nextIndex = currentIndex + 1;

        this.currentIndex = currentIndex;

        if (this.isVideo(previousIndex)) {
            this.video?.pause();
        }

        if (this.isVideo(currentIndex)) {
            this.video?.init();
        } else {
            this.loadImageForSlide(currentIndex);
        }

        if (this.isVideo(nextIndex)) {
            this.video?.init();
        } else {
            this.loadImageForSlide(nextIndex);
        }

        if (emitToOtherSubcomponents) {
            this.changeItem(currentIndex);
        }
    }

    private handleItemVisibilityChangedEvent(event: OcCarouselV1Events['oc-item-visibility-changed']) {
        const index = event.detail.visibleItem.index;
        const scrollSource = event.detail.scrollSource;
        if (event.detail.stageIsScrolling) {
            this.handleSlideChange(index, scrollSource !== 'move-method');
        } else {
            if (scrollSource !== 'move-method') {
                this.trackStageChange(index, scrollSource === 'pagination-dots' ? 'bullet' : 'swipe');
            }
        }
    }

    private trackStageChange(index: number, method: 'bullet' | 'swipe' | 'thumbnail') {
        const imageActivity = this.imageHasBeenChanged ? 'open_following' : 'open_first';
        const slideCount = this.getChildElements('.js_pdp_main-image__slide').length;
        if (this.isVideo(index)) {
            trackEvent({
                product_Video: ['open'],
                product_DifferentImageMethod: [method],
            });
        } else {
            const imageIndex = this.toImageIndex(index);
            const adjustedImageCount = slideCount - (this.hasVideo() ? 1 : 0);
            trackEvent({
                product_DifferentImageActivity: [imageActivity],
                product_DifferentImageChange: [`${imageIndex + 1}|${adjustedImageCount}`],
                product_DifferentImageMethod: [method],
            });
            this.imageHasBeenChanged = true;
        }
    }

    private getSlideContentElement(index: number): HTMLElement | undefined {
        return this.getChildElements('.js_pdp_main-image__slide-content')[index];
    }

    private loadImageForSlide(index: number): void {
        const slideContentElement = this.getSlideContentElement(index);
        if (slideContentElement?.dataset.imageLoaded === 'false') {
            slideContentElement.innerHTML = this.createZoomImageHtml(slideContentElement);
            slideContentElement.dataset.imageLoaded = 'true';
        }
    }

    private createZoomImageHtml(slideContentElement: HTMLElement): string {
        const { imageId = '', imageAlt = '' } = slideContentElement.dataset;
        const uri = getImageUrl(imageId);
        const escapedImageAlt = imageAlt.replace(/"/g, '&quot;');
        return zoomImageTemplate
            .replace(/\{\{!--.*?--\}\}/gs, '')
            .replace(/{{uri}}/g, uri)
            .replace(/{{id}}/g, imageId)
            .replace('{{../alt}}', escapedImageAlt)
            .replace('{{../seoImageName}}', this.seoImageName)
            .replace('{{#if @first}}fetchpriority="high"{{/if}}', '');
    }

    private getCarouselElement() {
        return this.getChildElement<HTMLOcCarouselV1Element>('.js_pdp_main-image__carousel');
    }
}
